<!--
	The delivered cut file, drawn the way the plotter cuts it.

	The real exported outline (1485 × 435 mm, 182 Bézier segments) strokes itself
	in once when the sheet scrolls into view, a plotter head riding the line; the
	nine bolt-recess cutouts follow, front to back. After that it is an
	instrument: point at a recess and the loupe shows that opening at scale,
	the lip traced off the scan against the 38 mm circle the first draft used.

	No JS / reduced motion: the finished drawing, no animation. The recess
	buttons under the sheet carry the same interaction for keyboard and touch.
-->
<script lang="ts">
	import { sheet, piece, outlinePath, recesses } from '$lib/content/case-ram';
	import { motionDisabled } from '$lib/motion/reduced';

	const DRAW_MS = 3400;
	/** Room around the sheet for the dimension lines. */
	const PAD = { top: 78, right: 96, bottom: 24, left: 24 };
	const viewBox = `${-PAD.left} ${-PAD.top} ${sheet.w + PAD.left + PAD.right} ${sheet.h + PAD.top + PAD.bottom}`;
	const LOUPE = 64; // mm across the loupe

	let version = $state<'v1' | 'v2'>('v2');
	let active = $state(3);
	/** Armed on mount: only then does the sheet start blank. No JS keeps the finished drawing. */
	let armed = $state(false);
	let drawn = $state(false);
	let root: HTMLElement;
	let head: SVGAnimateMotionElement | undefined = $state();

	const recess = $derived(recesses[active]);
	const pad2 = (n: number) => String(n + 1).padStart(2, '0');
	const totalCut = Math.round(piece.cutLength + recesses.reduce((sum, r) => sum + r.cut, 0));

	// One observer, fires once: the sheet draws when it arrives, never again.
	$effect(() => {
		if (motionDisabled()) {
			drawn = true;
			return;
		}
		armed = true;
		const io = new IntersectionObserver(
			([entry]) => {
				if (!entry.isIntersecting) return;
				drawn = true;
				head?.beginElement();
				io.disconnect();
			},
			{ threshold: 0.35 }
		);
		io.observe(root);
		return () => io.disconnect();
	});
</script>

<figure
	class="sheet"
	class:is-pre={armed && !drawn}
	class:is-drawn={drawn}
	bind:this={root}
	style:--draw="{DRAW_MS}ms"
>
	<div class="sheet__stage">
		<svg
			class="sheet__svg"
			{viewBox}
			role="img"
			aria-label="The delivered cut file: one long curved piece, 1485 by 435 millimetres, with nine bolt-recess cutouts along its length."
		>
			<defs>
				<pattern id="ram-grid" width="50" height="50" patternUnits="userSpaceOnUse">
					<path d="M50 0H0V50" class="grid-line" />
				</pattern>
				<!-- The site's ordered dither, as a fill: the piece reads as film, and the
				     stippled band under the cut line keeps scan-edge noise out of the eye. -->
				<pattern id="ram-dither" width="8" height="8" patternUnits="userSpaceOnUse">
					<rect x="0" y="0" width="2.6" height="2.6" />
					<rect x="4" y="4" width="2.6" height="2.6" />
				</pattern>
				<pattern id="ram-dither-fine" width="8" height="8" patternUnits="userSpaceOnUse">
					<rect x="0" y="0" width="2" height="2" />
				</pattern>
			</defs>
			<rect x="0" y="0" width={sheet.w} height={sheet.h} fill="url(#ram-grid)" class="grid" />

			<!-- Dimension lines: width along the top, height down the right. -->
			<g class="dim">
				<path
					d="M{piece.x} -30H{piece.x + piece.w}M{piece.x} -44V-16M{piece.x + piece.w} -44V-16"
				/>
				<text x={piece.x + 28} y="-44">1485 mm</text>
				<path
					d="M{sheet.w + 30} {piece.y}V{piece.y + piece.h}M{sheet.w + 16} {piece.y}H{sheet.w +
						44}M{sheet.w + 16} {piece.y + piece.h}H{sheet.w + 44}"
				/>
				<text
					x={sheet.w + 58}
					y={piece.y + piece.h / 2}
					text-anchor="middle"
					transform="rotate(90 {sheet.w + 58} {piece.y + piece.h / 2})">435 mm</text
				>
			</g>

			<path d={outlinePath} class="fill" />
			<path d={outlinePath} class="halo" />
			<path id="ram-outline" d={outlinePath} pathLength="1" class="cut" />

			{#each recesses as r, i (r.cx)}
				<!-- svelte-ignore a11y_no_static_element_interactions -->
				<g
					class="recess"
					class:recess--on={i === active}
					style:--i={i}
					onpointerenter={() => (active = i)}
				>
					<circle cx={r.cx} cy={r.cy} r="46" class="recess__hit" />
					{#if version === 'v2'}
						<path d={r.d} pathLength="1" class="recess__cut" />
					{:else}
						<circle cx={r.v1.cx} cy={r.v1.cy} r={r.v1.r} pathLength="1" class="recess__cut" />
					{/if}
					<circle cx={r.cx} cy={r.cy} r="34" class="recess__ring" />
					<text x={r.cx} y={r.cy - 46} text-anchor="middle" class="recess__no">{pad2(i)}</text>
				</g>
			{/each}

			<!-- The plotter head: rides the outline once, in step with the stroke. -->
			<g class="head">
				<circle r="9" />
				<path d="M-22 0H-13M13 0H22M0 -22V-13M0 13V22" />
				<animateMotion
					bind:this={head}
					dur="{DRAW_MS}ms"
					begin="indefinite"
					fill="freeze"
					calcMode="linear"
				>
					<mpath href="#ram-outline" />
				</animateMotion>
			</g>
		</svg>

		<!-- The loupe: the active recess at scale. -->
		<div class="loupe" aria-hidden="true">
			<svg
				viewBox="{recess.cx - LOUPE / 2} {recess.cy - LOUPE / 2} {LOUPE} {LOUPE}"
				class="loupe__svg"
			>
				<path d={outlinePath} class="loupe__edge" />
				<circle cx={recess.v1.cx} cy={recess.v1.cy} r={recess.v1.r} class="loupe__v1" />
				<path d={recess.d} class="loupe__v2" />
			</svg>
			<div class="loupe__text">
				<p class="loupe__cap">Recess {pad2(active)}</p>
				<p class="loupe__size">{recess.w} × {recess.h} mm</p>
				<p class="loupe__key"><span class="key key--v2"></span>traced lip, +3 mm</p>
				<p class="loupe__key"><span class="key key--v1"></span>38 mm circle</p>
			</div>
		</div>
	</div>

	<figcaption class="bar">
		<div class="toggle" role="group" aria-label="Draft of the cutouts">
			<button
				type="button"
				class="toggle__btn"
				class:toggle__btn--on={version === 'v1'}
				aria-pressed={version === 'v1'}
				onclick={() => (version = 'v1')}
			>
				<span class="toggle__index">v1</span> 38 mm circles
			</button>
			<button
				type="button"
				class="toggle__btn"
				class:toggle__btn--on={version === 'v2'}
				aria-pressed={version === 'v2'}
				onclick={() => (version = 'v2')}
			>
				<span class="toggle__index">v2</span> Traced from the scan
			</button>
		</div>

		<div class="picks" role="group" aria-label="Inspect a bolt recess">
			{#each recesses as r, i (r.cx)}
				<button
					type="button"
					class="pick"
					class:pick--on={i === active}
					aria-pressed={i === active}
					aria-label="Recess {i + 1}: {r.w} by {r.h} millimetres"
					onclick={() => (active = i)}
					onfocus={() => (active = i)}
				>
					{pad2(i)}
				</button>
			{/each}
		</div>

		<p class="bar__cap">
			ram1500-fender-flare.svg · as exported, no hand edits · 1 piece · 9 cutouts · {totalCut.toLocaleString(
				'en-US'
			)} mm of cut
		</p>
	</figcaption>
</figure>

<style>
	.sheet {
		margin-top: clamp(2.5rem, 5vw, 4rem);
	}
	.sheet__stage {
		position: relative;
		border: 1px solid var(--color-app-700);
		border-radius: var(--radius-win);
		background:
			radial-gradient(
				60% 80% at 50% 60%,
				color-mix(in srgb, var(--color-cut-400) 5%, transparent),
				transparent 70%
			),
			var(--color-app-900);
		padding: clamp(0.75rem, 2.5vw, 2rem);
		overflow: hidden;
	}
	.sheet__svg {
		display: block;
		width: 100%;
		height: auto;
		overflow: visible;
	}
	.grid {
		opacity: 0.9;
	}
	.grid-line {
		fill: none;
		stroke: rgba(255, 255, 255, 0.05);
		stroke-width: 1;
		vector-effect: non-scaling-stroke;
	}

	.dim path {
		fill: none;
		stroke: var(--color-bone-500);
		stroke-width: 1;
		vector-effect: non-scaling-stroke;
	}
	.dim text,
	.recess__no {
		font-family: var(--font-mono);
		font-size: 20px;
		letter-spacing: 0.06em;
		fill: var(--color-bone-500);
	}
	.is-pre .dim {
		opacity: 0;
	}
	.is-drawn .dim {
		transition: opacity 600ms var(--ease-out) calc(var(--draw) * 0.9);
	}

	/* The piece: film-grey fill arrives once the blade has been all the way round. */
	.fill {
		fill: color-mix(in srgb, var(--color-cut-400) 7%, transparent);
		stroke: none;
	}
	.fill {
		fill: url(#ram-dither-fine);
	}
	#ram-dither rect,
	#ram-dither-fine rect {
		fill: var(--color-cut-400);
	}
	#ram-dither-fine rect {
		opacity: 0.55;
	}
	.halo {
		fill: none;
		stroke: url(#ram-dither);
		stroke-width: 22;
		stroke-linejoin: round;
		opacity: 0.5;
	}
	.is-pre .fill,
	.is-pre .halo {
		opacity: 0;
	}
	.is-drawn .halo {
		transition: opacity 900ms var(--ease-out) var(--draw);
	}
	.is-drawn .fill {
		transition: opacity 900ms var(--ease-out) var(--draw);
	}
	.cut {
		fill: none;
		stroke: var(--color-cut-400);
		stroke-width: 2;
		stroke-linejoin: round;
		vector-effect: non-scaling-stroke;
		stroke-dasharray: 1;
		filter: drop-shadow(0 0 6px color-mix(in srgb, var(--color-cut-400) 45%, transparent));
	}
	.is-pre .cut {
		stroke-dashoffset: 1;
	}
	.is-drawn .cut {
		transition: stroke-dashoffset var(--draw) linear;
	}

	.recess {
		cursor: crosshair;
	}
	.recess__hit {
		fill: transparent;
	}
	.recess__cut {
		fill: var(--color-app-900);
		stroke: var(--color-bone-100);
		stroke-width: 1.5;
		vector-effect: non-scaling-stroke;
		stroke-dasharray: 1;
	}
	.is-pre .recess__cut {
		stroke-dashoffset: 1;
		fill-opacity: 0;
	}
	.is-drawn .recess__cut {
		transition:
			stroke-dashoffset 500ms var(--ease-out) calc(var(--draw) + var(--i) * 110ms),
			fill-opacity 400ms var(--ease-out) calc(var(--draw) + 300ms + var(--i) * 110ms),
			stroke var(--dur-med) var(--ease-out);
	}
	.recess--on .recess__cut {
		stroke: var(--color-cut-400);
	}
	.recess__ring {
		fill: none;
		stroke: var(--color-cut-400);
		stroke-width: 1;
		stroke-dasharray: 4 5;
		vector-effect: non-scaling-stroke;
		opacity: 0;
		transform-box: fill-box;
		transform-origin: center;
		transform: scale(0.7);
		transition:
			opacity var(--dur-med) var(--ease-out),
			transform 320ms var(--ease-out);
	}
	.recess__no {
		opacity: 0;
		transition: opacity var(--dur-med) var(--ease-out);
	}
	.recess--on .recess__ring {
		opacity: 0.9;
		transform: none;
	}
	.recess--on .recess__no {
		opacity: 1;
		fill: var(--color-cut-400);
	}

	.head {
		opacity: 0;
		pointer-events: none;
	}
	.head circle {
		fill: var(--color-bone-100);
	}
	.head path {
		fill: none;
		stroke: var(--color-bone-100);
		stroke-width: 1.5;
		vector-effect: non-scaling-stroke;
	}
	.is-drawn .head {
		animation: head-ride var(--draw) linear both;
	}
	@keyframes head-ride {
		0%,
		96% {
			opacity: 1;
		}
		100% {
			opacity: 0;
		}
	}

	/* The loupe sits in the arch's empty belly on desktop, under the sheet on a phone. */
	.loupe {
		position: absolute;
		left: 50%;
		top: clamp(1.5rem, 4.5vw, 4rem);
		display: flex;
		align-items: center;
		gap: 1.25rem;
		transform: translateX(-42%);
	}
	.is-pre .loupe,
	.is-pre .recess__ring,
	.is-pre .recess__no {
		opacity: 0;
	}
	.is-drawn .loupe {
		transition: opacity 600ms var(--ease-out) calc(var(--draw) + 900ms);
	}
	.loupe__svg {
		flex: none;
		display: block;
		width: clamp(6.5rem, 11vw, 10rem);
		aspect-ratio: 1;
		border-radius: 999px;
		border: 1px solid var(--color-app-700);
		background: var(--color-app-950);
		box-shadow: 0 0 0 5px color-mix(in srgb, var(--color-app-950) 70%, transparent);
	}
	.loupe__edge {
		fill: color-mix(in srgb, var(--color-cut-400) 7%, transparent);
		stroke: color-mix(in srgb, var(--color-cut-400) 55%, transparent);
		stroke-width: 1;
		vector-effect: non-scaling-stroke;
	}
	.loupe__v1 {
		fill: none;
		stroke: var(--color-bone-500);
		stroke-width: 1;
		stroke-dasharray: 3 3;
		vector-effect: non-scaling-stroke;
	}
	.loupe__v2 {
		fill: var(--color-app-950);
		stroke: var(--color-cut-400);
		stroke-width: 2;
		vector-effect: non-scaling-stroke;
	}
	.loupe__cap,
	.loupe__key,
	.bar__cap {
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.06em;
		color: var(--color-bone-500);
	}
	.loupe__cap {
		text-transform: uppercase;
		color: var(--color-cut-400);
	}
	.loupe__size {
		margin-block: 0.15rem 0.6rem;
		font-size: 1.25rem;
		font-weight: 600;
		letter-spacing: -0.01em;
		color: var(--color-bone-100);
		white-space: nowrap;
	}
	.loupe__key {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		line-height: 1.7;
		white-space: nowrap;
	}
	.key {
		display: inline-block;
		width: 0.9rem;
		height: 0;
		border-top: 2px solid var(--color-cut-400);
	}
	.key--v1 {
		border-top: 1px dashed var(--color-bone-500);
	}
	@media (max-width: 899px) {
		.loupe {
			position: static;
			transform: none;
			margin-top: 1rem;
		}
		.dim text,
		.recess__no {
			font-size: 34px;
		}
	}
	@media (max-width: 520px) {
		.toggle__btn {
			padding-inline: 0.7rem;
			letter-spacing: 0.02em;
		}
		.picks {
			display: grid;
			grid-template-columns: repeat(9, 1fr);
			width: 100%;
		}
		.pick {
			min-width: 0;
		}
	}

	.bar {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: space-between;
		gap: 1rem 2rem;
		margin-top: 1.25rem;
	}
	.toggle {
		display: inline-flex;
		padding: 3px;
		border: 1px solid var(--color-app-700);
		border-radius: 999px;
		background: var(--color-app-900);
	}
	.toggle__btn {
		display: inline-flex;
		align-items: baseline;
		gap: 0.45rem;
		padding: 0.5rem 0.95rem;
		border-radius: 999px;
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		white-space: nowrap;
		color: var(--color-bone-500);
		transition:
			color var(--dur-fast) var(--ease-out),
			background-color var(--dur-fast) var(--ease-out);
	}
	.toggle__btn:hover {
		color: var(--color-bone-100);
	}
	.toggle__btn--on,
	.toggle__btn--on:hover {
		background: var(--color-cut-400);
		color: var(--color-ink-900);
	}
	.toggle__index {
		opacity: 0.6;
	}
	.picks {
		display: flex;
		flex-wrap: wrap;
		gap: 0.25rem;
	}
	.pick {
		min-width: 2.25rem;
		padding: 0.5rem 0.4rem;
		border: 1px solid var(--color-app-700);
		border-radius: var(--radius-sm);
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.06em;
		color: var(--color-bone-500);
		transition:
			color var(--dur-fast) var(--ease-out),
			border-color var(--dur-fast) var(--ease-out);
	}
	.pick:hover {
		color: var(--color-bone-100);
	}
	.pick--on {
		border-color: var(--color-cut-400);
		color: var(--color-cut-400);
	}
	.bar__cap {
		flex-basis: 100%;
		text-wrap: pretty;
	}
	@media (min-width: 1100px) {
		.bar__cap {
			flex-basis: auto;
			margin-left: auto;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.cut,
		.recess__cut,
		.fill,
		.halo,
		.dim,
		.loupe,
		.recess__ring {
			transition: none !important;
		}
		.head {
			display: none;
		}
	}
</style>
